import { AlertCircle, CheckCircle2, Loader2, RotateCw, Video, X } from "lucide-react";
import {
  useVideoUploads,
  retryVideoUpload,
  dismissVideoUpload,
  cancelVideoUpload,
  type VideoUploadJob,
} from "../lib/video-upload";
import { cancelGalleryPreparing, useGalleryPreparing } from "../lib/native-media-picker";
import { cn } from "../lib/utils";

function statusText(job: VideoUploadJob): string {
  switch (job.status) {
    case "uploading":
      return `Prenošenje videa… ${job.progress}%`;
    case "processing":
      return "Obrada videa…";
    case "done":
      return "Objavljeno!";
    case "error":
      return job.error || "Prijenos nije uspio.";
  }
}

function UploadRow({ job }: { job: VideoUploadJob }) {
  const Icon =
    job.status === "done"
      ? CheckCircle2
      : job.status === "error"
        ? AlertCircle
        : job.status === "processing"
          ? Loader2
          : Video;

  return (
    <div className="pointer-events-auto rounded-lg border border-border/40 bg-[#0b1f2a]/95 px-3 py-2 shadow-lg backdrop-blur">
      <div className="flex items-center gap-2">
        <Icon
          className={cn(
            "h-4 w-4 shrink-0",
            job.status === "done" && "text-green-500",
            job.status === "error" && "text-red-500",
            job.status === "processing" && "animate-spin text-primary",
            job.status === "uploading" && "text-primary",
          )}
        />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-foreground">
            <span className="text-muted-foreground">{job.label} · </span>
            {statusText(job)}
          </p>
        </div>
        {job.status === "error" && (
          <button
            type="button"
            onClick={() => retryVideoUpload(job.id)}
            className="flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-primary hover:bg-primary/10"
          >
            <RotateCw className="h-3 w-3" /> Ponovi
          </button>
        )}
        {job.status !== "processing" && (
          <button
            type="button"
            aria-label={job.status === "uploading" ? "Otkaži prijenos" : "Zatvori"}
            onClick={() =>
              job.status === "uploading"
                ? cancelVideoUpload(job.id)
                : dismissVideoUpload(job.id)
            }
            className="rounded-md p-1 text-muted-foreground hover:bg-white/10 hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {(job.status === "uploading" || job.status === "processing") && (
        <div className="mt-1.5 h-1 w-full overflow-hidden rounded-full bg-white/10">
          <div
            className={cn(
              "h-full rounded-full bg-primary transition-[width] duration-300",
              job.status === "processing" && "w-full animate-pulse",
            )}
            style={job.status === "uploading" ? { width: `${job.progress}%` } : undefined}
          />
        </div>
      )}

      {job.status === "uploading" && (
        <p className="mt-1 text-[11px] leading-tight text-muted-foreground">
          Ne zatvarajte aplikaciju dok se video ne prenese. Možete je slobodno koristiti.
        </p>
      )}
      {job.status === "processing" && (
        <p className="mt-1 text-[11px] leading-tight text-muted-foreground">
          Prijenos je gotov — sada možete i izaći iz aplikacije.
        </p>
      )}
    </div>
  );
}

/** A picked video being got ready (e.g. downloaded from iCloud). */
function PreparingRow({ fraction }: { fraction: number }) {
  const pct = Math.round(fraction * 100);
  return (
    <div className="pointer-events-auto rounded-lg border border-border/40 bg-[#0b1f2a]/95 px-3 py-2 shadow-lg backdrop-blur">
      <div className="flex items-center gap-2">
        <Loader2 className="h-4 w-4 shrink-0 animate-spin text-primary" />
        <p className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">
          <span className="text-muted-foreground">Galerija · </span>
          Priprema videa…{pct > 0 ? ` ${pct}%` : ""}
        </p>
        <button
          type="button"
          aria-label="Odustani"
          onClick={cancelGalleryPreparing}
          className="rounded-md p-1 text-muted-foreground hover:bg-white/10 hover:text-foreground"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
      <div className="mt-1.5 h-1 w-full overflow-hidden rounded-full bg-white/10">
        <div
          className={cn(
            "h-full rounded-full bg-primary transition-[width] duration-300",
            pct === 0 && "w-full animate-pulse",
          )}
          style={pct > 0 ? { width: `${pct}%` } : undefined}
        />
      </div>
      <p className="mt-1 text-[11px] leading-tight text-muted-foreground">
        Video se preuzima s iClouda ili priprema na uređaju.
      </p>
    </div>
  );
}

/** Floating status for background video uploads, shown under the app header. */
export function VideoUploadBar() {
  const jobs = useVideoUploads();
  const preparing = useGalleryPreparing();
  if (jobs.length === 0 && preparing === null) return null;

  return (
    <div
      className="pointer-events-none fixed inset-x-0 z-[60] flex justify-center px-3"
      style={{ top: "calc(4rem + env(safe-area-inset-top) + 0.5rem)" }}
    >
      <div className="flex w-full max-w-md flex-col gap-2">
        {preparing !== null && <PreparingRow fraction={preparing} />}
        {jobs.map((job) => (
          <UploadRow key={job.id} job={job} />
        ))}
      </div>
    </div>
  );
}
